import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    NgZone,
    OnInit,
    ViewChild,
    QueryList,
    ViewChildren
} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {WorkbenchViewComponent} from '../../../shared/components/viewcontainers/workbench-view.component';
import {WORKBENCH_VIEW_CONFIG, WorkbenchViewConfig} from '../../../shared/types';
import {WorkbenchLayoutManager} from '../../../shared/services/workbench-layout-manager.service';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {WorkbenchTestService} from '../../../shared/services/workbench-test.service';
import {FindingId, ProjectId, TestRunId} from '@webmate-web-core/shared/types/ids';
import {filter, map, mergeMap, startWith, switchMap, take} from 'rxjs/operators';
import {WorkbenchContextService} from '../../../shared/services/workbench-context.service';
import moment from 'moment';
import {Finding, TestMgmtEventTypes, TestRun} from '@webmate-web-core/shared/types/testmgmt';
import {of} from 'rxjs';
import {faExternalLink, faSyncAlt} from '@fortawesome/pro-solid-svg-icons';
import {FindingFindingComponent} from '@webmate-web-core/shared/components/facts/finding-finding.component';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';

@UntilDestroy()
@Component({
    templateUrl: './workbench-adhoc-test-view.component.html',
    styleUrls: ['./workbench-adhoc-test-view.component.less']
})

export class WorkbenchAdhocTestViewComponent extends WorkbenchViewComponent implements OnInit, AfterViewInit {
    @ViewChildren(FindingFindingComponent) findingComponents!: QueryList<FindingFindingComponent>;
    @ViewChildren('findingEntry', { read: ElementRef }) findingHosts!: QueryList<ElementRef<HTMLElement>>;
    @ViewChildren('nameInput') nameInput!: QueryList<ElementRef<HTMLInputElement>>;
    @ViewChild('scrollContainer', { read: ElementRef }) scrollContainer?: ElementRef<HTMLElement>;

    projectId: ProjectId;

    newTestName = '';
    testRunStarting = false;
    manualTestPendingMessage = 'Starting manual test...';
    manualTestStartError?: string;

    activeTestRunId: TestRunId;
    activeTestRun?: TestRun;
    findings?: Finding[];
    finishingTestRun = false;
    testNameModificationInProgress = false;
    modifiedName?: string;
    modifyingName = false;

    viewIsReady: boolean = false;
    pendingFindingId: FindingId = undefined;
    private findingScrollCleanup?: () => void;

    constructor(layoutManager: WorkbenchLayoutManager,
                @Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<any>,
                private workbenchTestService: WorkbenchTestService,
                private testService: TestService,
                private workbenchContextService: WorkbenchContextService,
                private ngZone: NgZone,
                private notificationService: WmNotificationService,
                ){super(layoutManager, config);
    }

    ngOnInit() {
        this.projectId = this.workbenchContextService.getProjectId();
        this.workbenchContextService.subscribeWorkbenchContext$().pipe(switchMap(context => {
            this.activeTestRunId = context.testRunContext?.activeTestRun;
            if (this.activeTestRunId) {
                if (this.activeTestRun?.testRunId !== this.activeTestRunId) {
                    this.clearModifyName();
                }
                return this.testService.getTestRun(this.activeTestRunId).pipe(switchMap(testRun => {
                    this.activeTestRun = testRun;
                    this.testRunStarting = false;
                    this.manualTestStartError = undefined;
                    return this.testService.getFindingsForTestRun(this.activeTestRun.testRunId, true).fetch().pipe(map(r => r.result));
                }));
            } else {
                this.activeTestRun = undefined;
                this.findings = undefined;
                this.clearModifyName();
                this.updateManualTestPendingMessage(context.activeMode.kind === 'manual-test' ? context.activeMode.payload : undefined);
                return of(undefined);
            }
        }), untilDestroyed(this)).subscribe(findings => {
            this.findings = findings?.toSorted((a, b) => {
                return moment(a.creationTime).diff(b.creationTime);
            });
        });

        this.workbenchTestService.getFindingUpdateEventsForProject(this.projectId).pipe(untilDestroyed(this)).subscribe(event => {
           if (event.eventType === TestMgmtEventTypes.FindingDeleted) {
               const deletedFindings = event.findingIds as FindingId[];
               this.findings = this.findings?.filter(f => !deletedFindings.includes(f.id));
           } else if (event.eventType === TestMgmtEventTypes.FindingAdded && this.activeTestRunId) {
               // Findings created elsewhere (e.g. the analysis view) are not added through onFindingCreated,
               // so reconcile the list against the backend whenever a finding for the active test run appears.
               const addedFindings = (event.findings as Finding[]) ?? [];
               const hasNewFindingForActiveTestRun = addedFindings.some(f =>
                   f.testRunId === this.activeTestRunId && !this.findings?.some(existing => existing.id === f.id));
               if (hasNewFindingForActiveTestRun) {
                   this.testService.getFindingsForTestRun(this.activeTestRunId, true).fetch().pipe(
                       map(r => r.result),
                       take(1),
                       untilDestroyed(this)
                   ).subscribe(findings => {
                       this.findings = findings?.toSorted((a, b) => moment(a.creationTime).diff(b.creationTime));
                   });
               }
           }
        });
    }

    ngAfterViewInit() {
        this.nameInput.changes.pipe(untilDestroyed(this)).subscribe(() => {
            this.nameInput.first?.nativeElement.focus();
        });

        this.findingComponents.changes
            .pipe(untilDestroyed(this), startWith(this.findingComponents),
                filter(components => components.length > 0),
                switchMap(() => this.ngZone.onStable.pipe(take(1))),
                take(1)
            )
            .subscribe(() => {
                this.viewIsReady = true;
                if (this.pendingFindingId) {
                    const id = this.pendingFindingId;
                    this.pendingFindingId = undefined;
                    this.openFinding(id);
                }
            });
    }

    startTestRun() {
        this.testRunStarting = true;
        this.manualTestStartError = undefined;
        this.workbenchTestService.startAdHocTest(this.newTestName).pipe(take(1)).subscribe({
            next: () => {
                this.newTestName = '';
            },
            error: () => {
                this.testRunStarting = false;
                this.manualTestStartError = 'Manual test could not be started.';
            }
        });
    }

    finishTestRun() {
        if (!this.activeTestRunId || this.finishingTestRun) {
            return;
        }
        this.finishingTestRun = true;
        const oldActiveTestRunId = this.activeTestRunId;
        this.workbenchTestService.finishAdHocTest(this.activeTestRunId).pipe(mergeMap(() => {
            return this.testService.getTestRun(oldActiveTestRunId).pipe(take(1));
        })).subscribe(testRun => {
            this.finishingTestRun = false;
            this.workbenchContextService.clearActiveMode();
            this.notificationService.toastPersistentActionNotification(
                'Manual test finished.',
                'Open Test Run',
                () => this.openTestRunInWebmate(testRun.testRunId)
            );
        }, () => {
            this.finishingTestRun = false;
        });
    }

    clearModifyName() {
        this.modifiedName = undefined;
        this.modifyingName = false;
        this.testNameModificationInProgress = false;
    }

    saveTestNameModification() {
        const newName = this.modifiedName?.trim();
        if (!newName || newName === this.activeTestRun?.name || !this.activeTestRunId) {
            return;
        }
        this.testNameModificationInProgress = true;
        this.testService.setTestRunName(this.activeTestRunId, newName).pipe(take(1)).subscribe(() => {
            this.testService.refreshTestRun(this.activeTestRunId).pipe(take(1)).subscribe();
            this.activeTestRun = {
                ...this.activeTestRun!,
                name: newName
            };
            this.clearModifyName();
        }, error => {
            this.notificationService.toastErrorNotification('Could not set test name: ' + error.statusText);
            this.testNameModificationInProgress = false;
        });
    }

    onFindingCreated(finding: Finding) {
        // Show the new finding immediately instead of waiting for the backend update round-trip.
        const existing = this.findings ?? [];
        if (existing.some(f => f.id === finding.id)) {
            return;
        }
        this.findings = [...existing, finding].toSorted((a, b) => moment(a.creationTime).diff(b.creationTime));
    }

    deleteFinding(findingId: FindingId) {
        this.testService.deleteFinding(findingId).subscribe();
    }

    openTestRunInWebmate(testRunId: TestRunId) {
        window.open(`/projects/${this.projectId}/testlab/testruns/${testRunId}`, '_blank');
    }

    openFinding(id: FindingId) {
        if (this.findingComponents?.length && this.findingHosts?.length && this.viewIsReady) {
            this.findingComponents.forEach(finding => finding.findingCollapsed = finding.finding.id !== id);

            const index = this.findingComponents.toArray().findIndex(f => f.finding.id === id);
            const hostEl = this.findingHosts.toArray()[index]?.nativeElement;
            if (hostEl) {
                this.ngZone.onStable.pipe(take(1)).subscribe(() => {
                    this.scrollFindingToEnd(hostEl);
                });
            }
        } else {
            this.pendingFindingId = id;
        }
    }

    private scrollFindingToEnd(hostEl: HTMLElement) {
        const scrollContainer = this.scrollContainer?.nativeElement;
        if (!scrollContainer) {
            return;
        }

        this.stopFindingScrollTracking();

        const scrollToEnd = () => {
            const containerRect = scrollContainer.getBoundingClientRect();
            const hostRect = hostEl.getBoundingClientRect();
            const targetTop = scrollContainer.scrollTop + hostRect.bottom - containerRect.bottom + 8;
            const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;

            scrollContainer.scrollTo({
                top: Math.max(Math.min(targetTop, maxScrollTop), 0),
                behavior: 'smooth'
            });
        };

        scrollToEnd();

        const timeoutIds = [75, 200, 400, 700, 1100].map(delay => window.setTimeout(scrollToEnd, delay));
        let resizeObserver: ResizeObserver | undefined;

        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => scrollToEnd());
            resizeObserver.observe(hostEl);
        }

        const cleanupTimeoutId = window.setTimeout(() => {
            this.stopFindingScrollTracking();
        }, 1500);

        this.findingScrollCleanup = () => {
            timeoutIds.forEach(timeoutId => window.clearTimeout(timeoutId));
            window.clearTimeout(cleanupTimeoutId);
            resizeObserver?.disconnect();
            this.findingScrollCleanup = undefined;
        };
    }

    private stopFindingScrollTracking() {
        this.findingScrollCleanup?.();
    }

    protected readonly externalLink = faExternalLink;
    protected readonly pendingIcon = faSyncAlt;

    private updateManualTestPendingMessage(payload: {autoStart?: boolean; suppressAutoResume?: boolean} | undefined) {
        if (payload?.autoStart === false) {
            this.manualTestPendingMessage = 'Looking for an active manual test...';
            return;
        }
        this.manualTestPendingMessage = 'Starting manual test...';
    }
}
